/**
 * Capacity view — CapacityAllocations by resource and time window (Doc 17 §9).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, Users } from 'lucide-react';
import { NoPlanCycleState } from '../../components/planning/NoPlanCycleState';
import { PlanningListSkeleton } from '../../components/planning/PlanningListSkeleton';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { AUTHORIZE_POR_LABEL, START_DRAFT_VERSION_LABEL } from '../../lib/planning/vocabulary';
import { getFirstActiveCycleProvisional, createCapacityAllocation, canPerformPlanningGovernance, overrideExists, recordPlanningOverride, createDraftPlanVersion } from '../../lib/planningService';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { getTenantContext } from '../../lib/tenantContext';
import { listWorkspaceMembers } from '../../lib/workspaceService';
import { getCapacityPoolSummary } from '../../lib/capabilityService';
import type { CapacityAllocation } from '../../types/planning';

function loadAllocations(planVersionId: string): Promise<CapacityAllocation[]> {
  return getSupabaseClient()
    .from('capacity_allocations')
    .select('*')
    .eq('plan_version_id', planVersionId)
    .then(({ data: rows }) => (rows ?? []) as CapacityAllocation[]);
}

export default function PlanningCapacity() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams] = useSearchParams();
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [editingPlanVersionId, setEditingPlanVersionId] = useState<string | null>(null);
  const [draftPlanVersionId, setDraftPlanVersionId] = useState<string | null>(null);
  const [allocations, setAllocations] = useState<CapacityAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNonActiveCycle, setIsNonActiveCycle] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [newResourceId, setNewResourceId] = useState('');
  const [newAllocationPct, setNewAllocationPct] = useState('');
  const [newTimeStart, setNewTimeStart] = useState('');
  const [newTimeEnd, setNewTimeEnd] = useState('');
  const [addingAllocation, setAddingAllocation] = useState(false);
  const [allocationError, setAllocationError] = useState<string | null>(null);
  const [overridesExist, setOverridesExist] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideJustification, setOverrideJustification] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [poolSummary, setPoolSummary] = useState<{ personCount: number; totalHoursPerWeek: number } | null>(null);
  const [workspaceMembers, setWorkspaceMembers] = useState<{ userId: string; user: { name: string; email: string } }[]>([]);

  const workspaceId = getTenantContext()?.workspaceId ?? null;

  const refresh = useCallback((pvId: string) => {
    loadAllocations(pvId).then(setAllocations);
  }, []);

  useEffect(() => {
    const overrideCycleId = cycleIdFromParams ?? searchParams.get('cycleId') ?? selectedCycleId;
    getFirstActiveCycleProvisional(overrideCycleId ? { cycleId: overrideCycleId } : undefined).then(({ data }) => {
      if (!data) {
        setLoading(false);
        return;
      }
      setCycleId(data.cycle.id);
      setEditingPlanVersionId(data.editingPlanVersionId);
      setDraftPlanVersionId(data.draftPlanVersionId ?? null);
      setIsNonActiveCycle(data.isNonActiveCycle ?? false);
      loadAllocations(data.editingPlanVersionId ?? '').then((rows) => {
        setAllocations(rows);
        setLoading(false);
      });
    });
  }, [cycleIdFromParams, searchParams, selectedCycleId]);

  useEffect(() => {
    if (!editingPlanVersionId) return;
    overrideExists(editingPlanVersionId).then(({ exists }) => setOverridesExist(exists));
  }, [editingPlanVersionId]);

  useEffect(() => {
    if (!workspaceId) {
      setPoolSummary(null);
      setWorkspaceMembers([]);
      return;
    }
    getCapacityPoolSummary(workspaceId).then(({ data }) => setPoolSummary(data ?? null));
    listWorkspaceMembers(workspaceId).then((list) =>
      setWorkspaceMembers(list.map((m) => ({ userId: m.userId, user: m.user })))
    ).catch(() => setWorkspaceMembers([]));
  }, [workspaceId]);

  return (
    <div className="max-w-5xl">
      <PageHeader
        icon={<Users className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50 dark:bg-[var(--surface)]"
        title="Capacity"
        subtitle="Allocations by resource and time window. Planning allocation truth only."
      />

      {loading ? (
        <PlanningListSkeleton className="min-h-[320px]" />
      ) : !cycleId ? (
        <NoPlanCycleState onSelectCycle={(id) => { setSelectedCycleId(id); setLoading(true); }} />
      ) : (
        <div className="space-y-4">
          {isNonActiveCycle && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Viewing a non-active plan cycle. Activate it from{' '}
              <Link to="/planning/plans" className="font-medium underline">Planning Home</Link> when ready.
            </div>
          )}
          {poolSummary && (
            <div className="bg-primary-50 border border-primary-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-medium text-primary-900">Capacity pool</p>
                <p className="text-sm text-primary-700">
                  {poolSummary.personCount} people · {poolSummary.totalHoursPerWeek.toFixed(1)} hrs/week available
                </p>
              </div>
              <Link to="/planning/capacity-pool" className="text-sm font-medium text-primary-600 hover:underline">
                Edit pool →
              </Link>
            </div>
          )}
          {(() => {
            const byResource = allocations.reduce<Record<string, number>>((acc, a) => {
              acc[a.resource_identifier] = (acc[a.resource_identifier] ?? 0) + a.allocation_percentage;
              return acc;
            }, {});
            const overallocated = Object.entries(byResource).filter(([, sum]) => sum > 100);
            return overallocated.length > 0 ? (
              <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3 text-sm text-[var(--mustard)]">
                <p className="font-medium">Overallocation</p>
                <ul className="mt-1 list-disc list-inside">
                  {overallocated.map(([res, sum]) => (
                    <li key={res}>{res}: {sum}% allocated</li>
                  ))}
                </ul>
              </div>
            ) : null;
          })()}
          {overridesExist && (
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3 text-sm text-[var(--mustard)] flex items-center justify-between flex-wrap gap-2">
              <span className="font-medium">Capacity overrides in effect (recorded deviations)</span>
              <Link to={`/planning/governance?cycleId=${cycleId}`} className="text-primary-600 hover:underline font-medium">
                View in Governance →
              </Link>
            </div>
          )}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)] font-semibold text-[var(--text-pri)]">
              Capacity allocations ({allocations.length})
            </div>
            {allocations.length === 0 ? (
              <div className="px-4 py-6 text-[var(--text-sec)] text-sm">No capacity allocations. Add one below.</div>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {allocations.map((a) => (
                  <li key={a.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[var(--text-pri)]">{a.resource_identifier}</p>
                      <p className="text-xs text-[var(--text-ter)]">
                        {a.time_window_start ? new Date(a.time_window_start).toLocaleDateString() : '—'} to{' '}
                        {a.time_window_end ? new Date(a.time_window_end).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <span className="font-medium text-[var(--text-sec)]">{a.allocation_percentage}%</span>
                  </li>
                ))}
              </ul>
            )}
            {editingPlanVersionId && cycleId && canPerformPlanningGovernance() && (
              <div className="px-4 py-4 border-t border-[var(--rule)] bg-[var(--surface)] space-y-3">
                {!draftPlanVersionId && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      disabled={creatingDraft}
                      onClick={async () => {
                        setCreatingDraft(true);
                        setDraftError(null);
                        const { error } = await createDraftPlanVersion(cycleId);
                        setCreatingDraft(false);
                        if (error) {
                          setDraftError(error);
                          return;
                        }
                        getFirstActiveCycleProvisional().then(({ data: ctx }) => {
                          if (ctx) {
                            setEditingPlanVersionId(ctx.editingPlanVersionId);
                            setDraftPlanVersionId(ctx.draftPlanVersionId ?? null);
                            loadAllocations(ctx.editingPlanVersionId).then(setAllocations);
                          }
                        });
                      }}
                      className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                      {creatingDraft ? 'Creating draft…' : START_DRAFT_VERSION_LABEL}
                    </button>
                    <span className="text-xs text-[var(--text-ter)]">Clone the plan of record so edits go to a draft; then {AUTHORIZE_POR_LABEL.toLowerCase()}.</span>
                    {draftError && (
                      <span role="alert" className="text-xs text-[var(--coral)] basis-full">{draftError}</span>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-[var(--text-pri)]">Propose reallocation</p>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5">Add or change capacity allocations below; then Authorize POR from the plan to put the new plan version on record (Doc 17 §9).</p>
                </div>
                <p className="text-sm font-medium text-[var(--text-sec)]">Add allocation</p>
                {workspaceMembers.length > 0 && (
                  <p className="text-xs text-[var(--text-ter)] mb-1">Choose from pool or type a resource name below.</p>
                )}
                {workspaceMembers.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) {
                        const m = workspaceMembers.find((x) => x.userId === v || `${x.user.name || x.user.email}` === v);
                        if (m) setNewResourceId(m.user.name || m.user.email || m.userId);
                      }
                    }}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm w-full"
                  >
                    <option value="">— Select from pool —</option>
                    {workspaceMembers.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.user.name || m.user.email || m.userId.slice(0, 8)} ({m.user.email})
                      </option>
                    ))}
                  </select>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Resource identifier"
                    value={newResourceId}
                    onChange={(e) => { setNewResourceId(e.target.value); setAllocationError(null); }}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Allocation %"
                    value={newAllocationPct}
                    onChange={(e) => { setNewAllocationPct(e.target.value); setAllocationError(null); }}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="date"
                    placeholder="Time window start"
                    value={newTimeStart}
                    onChange={(e) => setNewTimeStart(e.target.value)}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="date"
                    placeholder="Time window end"
                    value={newTimeEnd}
                    onChange={(e) => setNewTimeEnd(e.target.value)}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                  />
                </div>
                {allocationError && <p className="text-xs text-[var(--coral)]">{allocationError}</p>}
                <button
                  type="button"
                  disabled={addingAllocation || !newResourceId.trim() || newAllocationPct === ''}
                  onClick={async () => {
                    const pct = Number(newAllocationPct);
                    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
                      setAllocationError('Enter a percentage between 0 and 100');
                      return;
                    }
                    setAllocationError(null);
                    setAddingAllocation(true);
                    const { error } = await createCapacityAllocation(editingPlanVersionId, {
                      resource_identifier: newResourceId.trim(),
                      allocation_percentage: pct,
                      time_window_start: newTimeStart || undefined,
                      time_window_end: newTimeEnd || undefined,
                    });
                    setAddingAllocation(false);
                    if (error) {
                      setAllocationError(error);
                      return;
                    }
                    setNewResourceId('');
                    setNewAllocationPct('');
                    setNewTimeStart('');
                    setNewTimeEnd('');
                    refresh(editingPlanVersionId);
                  }}
                  className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {addingAllocation ? 'Adding…' : 'Add allocation'}
                </button>
              </div>
            )}
            {editingPlanVersionId && cycleId && canPerformPlanningGovernance() && (
              <div className="px-4 py-3 border-t border-[var(--rule)] bg-[var(--surface)]">
                <button
                  type="button"
                  onClick={() => { setShowOverrideModal(true); setOverrideJustification(''); }}
                  className="text-sm text-[var(--mustard)] hover:underline font-medium"
                >
                  Request capacity override
                </button>
              </div>
            )}
            <div className="px-4 py-3 border-t border-[var(--rule)] bg-[var(--surface)]">
              <Link to={`/planning/plans/${cycleId}`} className="text-sm text-primary-600 hover:underline">
                View plan →
              </Link>
            </div>
          </div>
        </div>
      )}

      {showOverrideModal && editingPlanVersionId && cycleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" role="dialog" aria-label="Request capacity override">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-4 space-y-4">
            <h3 className="font-semibold text-[var(--text-pri)]">Request capacity override</h3>
            <p className="text-sm text-[var(--text-sec)]">Record a deviation (e.g. allowed overallocation). This will be audited and shown in Governance.</p>
            <textarea
              value={overrideJustification}
              onChange={(e) => setOverrideJustification(e.target.value)}
              placeholder="Justification (required)"
              rows={3}
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
              required
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowOverrideModal(false)} className="px-3 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingOverride || !overrideJustification.trim()}
                onClick={async () => {
                  setSubmittingOverride(true);
                  const { error } = await recordPlanningOverride({
                    plan_cycle_id: cycleId,
                    plan_version_id: editingPlanVersionId,
                    override_type: 'CapacityOverride',
                    justification: overrideJustification.trim(),
                  });
                  setSubmittingOverride(false);
                  if (error) {
                    setAllocationError(error);
                    return;
                  }
                  setShowOverrideModal(false);
                  setOverrideJustification('');
                  overrideExists(editingPlanVersionId).then(({ exists }) => setOverridesExist(exists));
                }}
                className="px-3 py-2 bg-[var(--mustard)] text-white text-sm font-medium hover:bg-[var(--mustard)] disabled:opacity-50"
              >
                {submittingOverride ? 'Recording…' : 'Record override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
